import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/use-toast';
import { api } from '@/services/api';
import { DocumentHeader } from '@/components/document-editor/document-header';
import DocumentSidebar from '@/components/document-editor/document-sidebar';
import DocumentCanvas, { PlacedField } from '@/components/document-editor/document-canvas';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
} from '@/components/ui/dialog';
import { Loader2, Upload, ChevronLeft, CheckCircle, Bell, ArrowUpCircle } from 'lucide-react';
import { useDocumentCategories } from '@/hooks/useDocumentCategories';
import { usePlan } from '@/contexts/PlanContext';
import { Link } from 'react-router-dom';
import { Recipient } from '@/types/document-types';

// Field type keys for translation lookup
const FIELD_TYPE_KEYS: Record<string, string> = {
  signature: 'editor_field_signature',
  initial: 'editor_field_initial',
  date: 'editor_field_date',
  name: 'editor_field_name',
  email: 'editor_field_email',
  city: 'editor_field_city',
  position: 'editor_field_position',
  province: 'editor_field_province',
  state: 'editor_field_state',
};

// Define the type for signing workflow
type SigningWorkflow = 'parallel' | 'sequential';

// Define the types for document data
interface DocumentData {
  id?: string;
  title: string;
  category_id: string;
  notes?: string;
  path?: string;
  file_path?: string;
  url?: string;
  status?: string;
  created_at?: string;
  updated_at?: string;
  category?: {
    id: string;
    name: string;
  };
  signing_workflow: SigningWorkflow;
  recipients: Recipient[];
  fields: PlacedField[];
  signing_requests?: any[];
  file?: File;
}

// Document header data interface for internal use
interface DocumentHeaderData {
  title: string;
  categoryId: string;
  notes?: string;
}

interface ApiResponse<T> {
  message: string;
  data?: T;
  errors?: Record<string, string[]>;
}

const DocumentEditor: React.FC = () => {
  const { t } = useTranslation(['documents', 'common']);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Categories from custom hook
  const { categories } = useDocumentCategories();

  // Memoized field type labels using translations
  const fieldTypeLabels: Record<string, string> = useMemo(() => {
    const labels: Record<string, string> = {};
    for (const [key, translationKey] of Object.entries(FIELD_TYPE_KEYS)) {
      labels[key] = t(`documents:${translationKey}`);
    }
    return labels;
  }, [t]);

  // State variables
  const [document, setDocument] = useState<DocumentData>({
    title: '',
    category_id: '',
    notes: '',
    signing_workflow: 'parallel',
    recipients: [],
    fields: []
  });
  
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [saving, setSaving] = useState(false);
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentUrl, setDocumentUrl] = useState<string>('');
  const [documentId, setDocumentId] = useState<string | undefined>(id);
  const [sendSuccess, setSendSuccess] = useState(false);

  const { canUse } = usePlan();
  const canUseOpenNotifications = canUse('open_notifications');

  // Helper function to show toast messages
  const showToast = useCallback(({ title, description, variant = 'default' }: { 
    title: string; 
    description: string; 
    variant?: 'default' | 'destructive' | 'success' 
  }) => {
    toast({
      title,
      description,
      variant
    });
  }, [toast]);

  // Fetch document data from the server
  const fetchDocument = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      const response = await api.get<any>(`/documents/${id}`) as any;
      
      // API returns { document: {...} } - response is the raw JSON
      const docData = response?.document || response?.data?.document || response?.data?.data || response?.data;
      console.log('Fetched document data:', docData);
      if (docData) {

        // Ensure fields have the correct property names and add label
        const formattedFields = (docData.fields || []).map((field: any) => {
          const fieldType = field.type || field.field_type;
          const signedData = field.signed_data;
          return {
            ...field,
            // Convert backend property names to match PlacedField interface
            type: fieldType,
            label: field.label || fieldTypeLabels[fieldType] || fieldType?.charAt(0).toUpperCase() + fieldType?.slice(1) || 'Field',
            pageNumber: field.pageNumber || field.page_number || field.page || 1,
            recipientId: (field.recipientId || field.recipient_id)?.toString(),
            x: field.x ?? field.x_position,
            y: field.y ?? field.y_position,
            // Include signature data from signed fields
            signatureImage: signedData?.signature_image_url || null,
            fullName: signedData?.signatory_name || null,
          };
        });

        // Map signing_requests to recipients format (for sent documents)
        const recipientsFromSigningRequests = (docData.signing_requests || []).map((sr: any) => ({
          id: sr.signatory_id?.toString() || sr.signatory?.id?.toString(),
          name: sr.signatory ? `${sr.signatory.first_name} ${sr.signatory.last_name}` : 'Unknown',
          email: sr.signatory?.email || '',
          isExpanded: true,
          fields: [],
          isCurrentUser: false,
        }));

        // Map recipients directly (for draft documents)
        // Use document_recipients.id as the ID since DocumentField.recipient_id references it
        const recipientsFromDocRecipients = (docData.recipients || []).map((r: any) => ({
          id: r.id?.toString(),
          name: r.name || 'Unknown',
          email: r.email || '',
          locale: r.locale || undefined,
          sent_via: r.sent_via || undefined,
          phone: r.phone || undefined,
          isExpanded: true,
          fields: [],
          isCurrentUser: false,
        }));

        // Use signing_requests recipients if available, otherwise use direct recipients
        const formattedRecipients = recipientsFromSigningRequests.length > 0 
          ? recipientsFromSigningRequests 
          : recipientsFromDocRecipients;

        setDocument({
          ...docData,
          fields: formattedFields,
          recipients: formattedRecipients
        });

        // Fetch the document file (backend uses file_path)
        if (docData.file_path || (docData as any).path) {
          fetchDocumentFile(id);
        }
      }
    } catch (error) {
      console.error('Error fetching document:', error);
      showToast({ 
        title: 'Error', 
        description: 'Failed to fetch document details', 
        variant: 'destructive' 
      });
    } finally {
      setLoading(false);
    }
  }, [id, showToast]);

  // Fetch the document file
  const fetchDocumentFile = async (documentId: string) => {
    try {
      const response = await api.get<Blob>(`/documents/${documentId}/download`, {
        responseType: 'blob'
      });

      if (response.data) {
        // response.data is already a Blob from the API
        const url = URL.createObjectURL(response.data);
        setDocumentUrl(url);
      }
    } catch (error) {
      console.error('Error fetching document file:', error);
      showToast({ 
        title: 'Error', 
        description: 'Failed to fetch document file', 
        variant: 'destructive' 
      });
    }
  };

  // Maximum file size: 50MB (must match backend validation)
  const MAX_FILE_SIZE_MB = 50;
  const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

  // Handle file upload
  const handleFileSelect = useCallback(async (file: File) => {
    if (file.size > MAX_FILE_SIZE_BYTES) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
      showToast({
        title: 'File too large',
        description: `This file is ${fileSizeMB} MB. The maximum allowed size is ${MAX_FILE_SIZE_MB} MB. Please consider splitting the file into smaller parts or compressing it before uploading.`,
        variant: 'destructive',
      });
      return;
    }

    // Set document title from filename (without extension)
    const filenameWithoutExtension = file.name.replace(/\.[^/.]+$/, '');
    setDocument(prev => ({
      ...prev,
      title: filenameWithoutExtension
    }));

    const ext = file.name.split('.').pop()?.toLowerCase();
    const allowedExtensions = ['pdf', 'doc', 'docx'];
    if (!ext || !allowedExtensions.includes(ext)) {
      showToast({
        title: t('documents:upload_error_title', 'Unsupported file type'),
        description: t('documents:upload_error_unsupported_type', {
          defaultValue: 'Only PDF, DOC, and DOCX files are supported. Please select a valid document.',
        }),
        variant: 'destructive',
      });
      return;
    }

    if (ext === 'doc' || ext === 'docx') {
      // DOC/DOCX: convert via backend, then preview the returned PDF
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append('document', file);
        const response = await api.post<Blob>('/documents/convert', formData, {
          responseType: 'blob',
        });
        if (response.data) {
          const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
          const pdfFile = new File([pdfBlob], filenameWithoutExtension + '.pdf', { type: 'application/pdf' });
          setDocumentFile(pdfFile);
          const url = URL.createObjectURL(pdfBlob);
          setDocumentUrl(url);
        }
      } catch (error: any) {
        showToast({
          title: 'Conversion failed',
          description: error.response?.data?.message || 'Failed to convert document to PDF.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    } else {
      // PDF: use directly
      setDocumentFile(file);
      const url = URL.createObjectURL(file);
      setDocumentUrl(url);
    }
  }, [showToast]);

  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    handleFileSelect(file);
  }, [handleFileSelect]);

  // Trigger file input click
  const handleUploadClick = useCallback(() => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  // Handle document header data changes
  const handleHeaderChange = useCallback((headerData: DocumentHeaderData) => {
    setDocument(prev => ({
      ...prev,
      title: headerData.title,
      category_id: headerData.categoryId,
      notes: headerData.notes || ''
    }));
  }, []);

  // Handle workflow change
  const handleWorkflowChange = useCallback((workflow: SigningWorkflow) => {
    setDocument(prev => ({
      ...prev,
      signing_workflow: workflow
    }));
  }, []);

  // Handle recipients change and filter fields for removed recipients
  const handleRecipientsChange = useCallback((recipients: Recipient[]) => {
    setDocument(prev => {
      const recipientIds = new Set(recipients.map(r => r.id));
      const filteredFields = prev.fields.filter(field => recipientIds.has(field.recipientId || ''));
      return {
        ...prev,
        recipients,
        fields: filteredFields
      };
    });
  }, []);

  // Handle fields change
  const handleFieldsChange = useCallback((fields: PlacedField[]) => {
    setDocument(prev => ({
      ...prev,
      fields
    }));
  }, []);



  // Save the document
  const handleSave = async (headerData?: { title: string; categoryId: string; notes: string }) => {
    if (saving) return;

    try {
      setSaving(true);
      
      // Use header data if provided, otherwise fall back to document state
      const title = headerData?.title || document.title;
      const categoryId = headerData ? headerData.categoryId : (document.category_id || '');
      const notes = headerData?.notes || document.notes || '';
      
      const formData = new FormData();
      formData.append('title', title);
      if (categoryId) {
        formData.append('category_id', categoryId);
      }
      formData.append('notes', notes);
      formData.append('signing_workflow', document.signing_workflow);
      
      // Add recipients - always send clear flag so backend processes even if empty
      formData.append('clear_recipients', 'true');
      document.recipients.forEach((recipient, index) => {
        formData.append(`recipients[${index}][name]`, recipient.name);
        formData.append(`recipients[${index}][email]`, recipient.email);
      });

      // Add fields with correct property names matching backend expectations
      // Filter out fields without required data
      const validFields = document.fields.filter(field => field.type && field.recipientId);
      // Always send clear_fields flag so backend knows to process fields even if empty
      formData.append('clear_fields', 'true');
      validFields.forEach((field, index) => {
        // Find the recipient's email so the backend can map to the new DB recipient ID
        const recipient = document.recipients.find(r => r.id === field.recipientId);
        formData.append(`fields[${index}][field_type]`, field.type);
        formData.append(`fields[${index}][x_position]`, (field.x ?? 0).toString());
        formData.append(`fields[${index}][y_position]`, (field.y ?? 0).toString());
        formData.append(`fields[${index}][page_number]`, (field.pageNumber ?? 1).toString());
        formData.append(`fields[${index}][width]`, (field.width || 100).toString());
        formData.append(`fields[${index}][height]`, (field.height || 40).toString());
        formData.append(`fields[${index}][recipient_id]`, recipient?.email || field.recipientId!);
      });

      // Add file if it exists (backend expects 'document' field name)
      if (documentFile) {
        formData.append('document', documentFile);
      }

      // If document is not in draft status, send revert_to_draft flag
      if (documentId && document.status && document.status !== 'draft') {
        formData.append('revert_to_draft', '1');
      }

      // Send the request - use documentId state instead of id from useParams
      // For updates, use POST with _method override (Laravel doesn't parse FormData on PUT)
      if (documentId) {
        formData.append('_method', 'PUT');
      }
      const url = documentId ? `/documents/${documentId}` : '/documents';
      const response = await api.post<ApiResponse<DocumentData>>(url, formData);

      // Backend returns { document: ... } or { data: ... } depending on endpoint
      const updatedDoc = (response as any).document || (response as any).data?.document || response.data;
      
      if (updatedDoc) {
        // Format fields from backend response to match PlacedField interface
        const formattedFields = (updatedDoc.fields || []).map((field: any) => {
          const fieldType = field.type || field.field_type;
          return {
            ...field,
            type: fieldType,
            label: field.label || fieldTypeLabels[fieldType] || fieldType?.charAt(0).toUpperCase() + fieldType?.slice(1) || 'Field',
            pageNumber: field.pageNumber || field.page_number || field.page || 1,
            recipientId: (field.recipientId || field.recipient_id)?.toString(),
            x: field.x ?? field.x_position,
            y: field.y ?? field.y_position,
          };
        });

        // Format recipients from backend response to match frontend Recipient interface
        const formattedRecipients = (updatedDoc.recipients || []).map((r: any) => ({
          id: r.id?.toString(),
          name: r.name || 'Unknown',
          email: r.email || '',
          locale: r.locale || undefined,
          isExpanded: true,
          fields: [],
          isCurrentUser: false,
        }));

        setDocument(prev => ({
          ...prev,
          ...updatedDoc,
          fields: formattedFields,
          recipients: formattedRecipients,
        }));

        showToast({ 
          title: 'Success', 
          description: 'Document saved successfully',
          variant: 'success'
        });

        // If this is a new document, update URL and state without reload
        if (!documentId && updatedDoc.id) {
          setDocumentId(updatedDoc.id);
          window.history.replaceState(null, '', `/document-editor/${updatedDoc.id}`);
        }
      }
    } catch (error: any) {
      console.error('Error saving document:', error);
      showToast({ 
        title: 'Error', 
        description: error.response?.data?.message || 'Failed to save document', 
        variant: 'destructive' 
      });
    } finally {
      setSaving(false);
    }
  };

  // Send the document for signing
  const handleSend = async () => {
    if (sending || !documentId) return;
    
    // Validate that we have recipients
    if (!document.recipients || document.recipients.length === 0) {
      showToast({ 
        title: 'Validation Error', 
        description: 'Please add at least one recipient before sending', 
        variant: 'destructive' 
      });
      return;
    }
    
    try {
      setSending(true);
      
      // Prepare recipients data for the send request
      const recipientsData = document.recipients.map((recipient, index) => ({
        id: recipient.id,
        email: recipient.email,
        name: recipient.name,
        order: index + 1,
        phone: recipient.phone,
        sent_via: recipient.sent_via,
      }));
      
      // Send the document with recipients and workflow
      const response = await api.post<ApiResponse<any>>(`/documents/${documentId}/send`, {
        recipients: recipientsData,
        signing_workflow: document.signing_workflow || 'parallel'
      });
      
      if (response) {
        // Update the signing requests badge count in the sidebar
        window.dispatchEvent(new Event('signing-count-changed'));
        
        // Show success dialog with upgrade prompt
        setSendSuccess(true);
      }
    } catch (error: any) {
      console.error('Error sending document:', error);
      showToast({ 
        title: 'Error', 
        description: error.message || 'Failed to send document', 
        variant: 'destructive' 
      });
    } finally {
      setSending(false);
    }
  };

  // Go back to documents list
  const handleBack = () => {
    navigate('/documents');
  };

  // Initialize
  useEffect(() => {
    if (id) {
      fetchDocument();
    }

    // Clean up URLs when component unmounts
    return () => {
      if (documentUrl) {
        URL.revokeObjectURL(documentUrl);
      }
    };
  }, [id, fetchDocument]);

  // Render upload prompt if no document (but not when we have an ID and are about to fetch)
  if (!documentUrl && !loading && !id) {
    return (
      <div className="flex flex-col items-center justify-center flex-1 p-4">
        <div className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg w-full max-w-md">
          <Button
            variant="outline"
            className="mb-2"
            onClick={handleBack}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            {t('documents:editor_back_to_documents')}
          </Button>

          <h2 className="text-xl font-semibold mb-4">{t('documents:editor_upload_title')}</h2>
          <p className="text-gray-500 mb-2 text-center">
            {t('documents:editor_upload_description')}
          </p>
          <p className="text-xs text-muted-foreground mb-6 text-center">
            {t('documents:editor_upload_max_size', { size: MAX_FILE_SIZE_MB })}
          </p>

          <input
            type="file"
            ref={fileInputRef}
            accept=".pdf,.doc,.docx"
            className="hidden"
            onChange={handleFileUpload}
          />

          <Button onClick={handleUploadClick} className="flex items-center">
            <Upload className="h-4 w-4 mr-2" />
            {t('documents:editor_select_pdf')}
          </Button>
        </div>
      </div>
    );
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin mb-4" />
        <p>{t('documents:editor_loading')}</p>
      </div>
    );
  }

  // Render document editor
  return (
    <>
    <div className="flex flex-col h-screen">
        {/* Document Header */}
        <DocumentHeader
          documentName={document.title}
          documentPath={document.path}
          categories={categories}
          onBack={handleBack}
          onSave={(data) => handleSave(data)}
          onSend={() => handleSend()}
          disableSave={saving}
          disableSend={sending || !documentId}
          initialTitle={document.title}
          initialCategory={document.category_id?.toString() || ''}
          initialNotes={document.notes || ''}
        />

        <div className="flex flex-1 overflow-hidden">
          {/* Document Sidebar */}
          <DocumentSidebar
            existingRecipients={document.recipients}
            onRecipientsChange={handleRecipientsChange}
            initialWorkflow={document.signing_workflow}
            onWorkflowChange={handleWorkflowChange}
          />

          {/* Document Canvas */}
          <div className="flex-1 overflow-auto bg-gray-100">
            <DocumentCanvas
              documentUrl={documentUrl}
              fields={document.fields}
              onFieldsChange={handleFieldsChange}
              onDocumentUpload={handleFileSelect}
            />
          </div>
        </div>
      </div>

      {/* Send success dialog */}
      <Dialog open={sendSuccess} onOpenChange={(open) => { if (!open) navigate('/documents'); }}>
        <DialogContent className="sm:max-w-[420px]">
          <div className="flex flex-col items-center py-6 gap-4">
            <div className="rounded-full bg-green-100 p-3">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <div className="text-center">
              <h3 className="text-lg font-semibold text-gray-900">{t('documents:send_success_title')}</h3>
              <p className="text-sm text-muted-foreground mt-1">
                {t('documents:send_success_description', { title: document.title, count: document.recipients.length })}
              </p>
            </div>

            {!canUseOpenNotifications && (
              <div className="w-full rounded-lg border border-amber-200 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <div className="rounded-full bg-amber-100 p-1.5 mt-0.5">
                    <Bell className="h-4 w-4 text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-medium text-amber-900">
                      {t('documents:send_open_notify_title')}
                    </p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      {t('documents:send_open_notify_description')}
                    </p>
                    <Link to="/settings?tab=billing" onClick={() => navigate('/settings?tab=billing')}>
                      <Button size="sm" className="mt-2 bg-amber-600 hover:bg-amber-700 text-white h-7 text-xs">
                        <ArrowUpCircle className="h-3 w-3 mr-1" />
                        {t('billing:upgrade.button', 'Upgrade Plan')}
                      </Button>
                    </Link>
                  </div>
                </div>
              </div>
            )}

            <Button onClick={() => navigate('/documents')} className="w-full">
              {t('common:actions.done', 'Done')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DocumentEditor;
